import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import CheckOutlined from '@mui/icons-material/CheckOutlined';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import {
  type ChangeEvent,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { dateWindowFromRange, latestSettledDate, nextUpdateLabel } from '../../lib/dateUtils';
import { ControlButton } from './ControlButton';
import { DATE_PRESETS } from './constants';
import { FilterOptionMenu } from './FilterOptionMenu';
import { ScopeLeadingKpiControl } from './LeadingKpiControl';
import { FILTER_MENU_SLOT_PROPS, filterMenuItemSx } from './menuStyles';
import type {
  DateFieldRowProps,
  DateRangeControlProps,
  MultiScopeSelectControlProps,
  ScopeRowProps,
} from './types';
import { useScopeOptions } from './useScopeOptions';
import { useScopeAccounts } from './useScopeSelection';

export function MultiScopeSelectControl({
  label,
  placeholderLabel,
  options,
  selected,
  onChange,
  icon,
  disabled = false,
  centered = false,
  onRemove,
  width,
  showSingleOptionName = false,
}: MultiScopeSelectControlProps): ReactNode {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const selectedOptions = options.filter((option) => selected.includes(option.id));
  const [firstOption, ...restOptions] = selectedOptions;
  // With a single option, "all" is that option: name it so nobody thinks they still have to pick.
  const onlyOption = showSingleOptionName && options.length === 1 ? options[0] : undefined;
  const summary =
    firstOption === undefined
      ? (onlyOption?.label ?? placeholderLabel)
      : restOptions.length === 0
        ? firstOption.label
        : `${firstOption.label} +${restOptions.length}`;

  return (
    <>
      <ControlButton
        label={label}
        value={summary}
        open={anchor !== null}
        onClick={(event: MouseEvent<HTMLButtonElement>) => setAnchor(event.currentTarget)}
        labelFirst
        disabled={disabled}
        centered={centered}
        {...(icon ? { icon } : {})}
        {...(onRemove ? { onRemove } : {})}
        {...(width === undefined ? {} : { fixedWidth: true, width })}
      />
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        slotProps={FILTER_MENU_SLOT_PROPS}
      >
        <FilterOptionMenu label={label} options={options} selected={selected} onChange={onChange} />
      </Menu>
    </>
  );
}

function DateFieldRow({ label, value, min, max, onChange }: DateFieldRowProps): ReactNode {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const next = event.target.value;
    setDraft(next);
    // A native date input reports '' until all of yyyy/mm/dd are filled in, so
    // committing every change would push a half-typed date up as the real value.
    if (next) onChange(next);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        component="span"
        sx={{
          width: 32,
          flexShrink: 0,
          fontSize: 9,
          fontWeight: 700,
          textTransform: 'uppercase',
          color: 'text.secondary',
        }}
      >
        {label}
      </Box>
      <Box
        component="input"
        type="date"
        aria-label={`${label} date`}
        value={draft}
        min={min}
        max={max}
        onChange={handleChange}
        onBlur={() => {
          if (draft !== value) onChange(draft);
        }}
        sx={(theme) => ({
          flex: 1,
          minWidth: 0,
          height: 32,
          px: 1,
          border: '1px solid',
          borderColor: theme.palette.border.strong,
          borderRadius: 1,
          color: theme.palette.text.primary,
          bgcolor: theme.palette.background.paper,
          '&:focus': {
            outline: 'none',
            borderColor: theme.palette.primary.main,
          },
        })}
      />
    </Box>
  );
}

function formatDate(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

export function DateRangeControl({ value, onChange, resolved }: DateRangeControlProps): ReactNode {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const preset = DATE_PRESETS.find((option) => option.id === value.preset);
  const isCustom = value.mode === 'custom';
  const cutoff = latestSettledDate(new Date());
  const customSummary =
    value.from && value.to
      ? [value.from, value.to].sort().map(formatDate).join(' – ')
      : value.from
        ? `From ${formatDate(value.from)}`
        : value.to
          ? `Until ${formatDate(value.to)}`
          : 'Custom range';
  const resolvedSummary = resolved ? `${formatDate(resolved[0])} – ${formatDate(resolved[1])}` : '';
  const summary = isCustom
    ? customSummary
    : preset
      ? resolvedSummary
        ? `${preset.label} · ${resolvedSummary}`
        : preset.label
      : 'Select';

  return (
    <>
      <ControlButton
        label="Date Range"
        value={summary}
        icon={CalendarTodayOutlined}
        open={anchor !== null}
        onClick={(event: MouseEvent<HTMLButtonElement>) => setAnchor(event.currentTarget)}
        labelFirst
      />
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        slotProps={FILTER_MENU_SLOT_PROPS}
      >
        {!isCustom && resolved ? (
          <Box sx={{ px: 2, pt: 1, pb: 1.5, fontSize: 11, color: 'text.secondary' }}>
            {`Data cutoff ${formatDate(resolved[1])} · next update ${nextUpdateLabel(new Date())}`}
          </Box>
        ) : null}
        {DATE_PRESETS.map((option) => (
          <MenuItem
            key={option.id}
            selected={!isCustom && value.preset === option.id}
            onClick={() => {
              onChange({ mode: 'preset', preset: option.id, from: '', to: '' });
              setAnchor(null);
            }}
            sx={filterMenuItemSx}
          >
            <ListItemText primary={option.label} sx={{ m: 0 }} />
            {!isCustom && value.preset === option.id ? <CheckOutlined fontSize="small" /> : null}
          </MenuItem>
        ))}
        <Divider />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            px: 2,
            py: 2,
            width: 280,
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: 9,
              fontWeight: 700,
              textTransform: 'uppercase',
              color: 'text.disabled',
            }}
          >
            Custom range
          </Box>
          <DateFieldRow
            label="From"
            value={value.from}
            max={value.to || cutoff}
            onChange={(from) => onChange({ ...value, mode: 'custom', from })}
          />
          <DateFieldRow
            label="To"
            value={value.to}
            min={value.from}
            max={cutoff}
            onChange={(to) => onChange({ ...value, mode: 'custom', to })}
          />
        </Box>
      </Menu>
    </>
  );
}

export function ScopeRow({
  scope,
  onChange,
  creativeTypeOptions,
  extraControls,
}: ScopeRowProps): ReactNode {
  const { brandOptions, platformOptions, accountOptions } = useScopeOptions(scope);
  const resolved = useMemo(() => dateWindowFromRange(scope.range, new Date()), [scope.range]);
  const { accountIds } = useScopeAccounts(scope);

  return (
    <>
      <ScopeLeadingKpiControl scope={scope} onChange={onChange} accountIds={accountIds} />
      <MultiScopeSelectControl
        label="Brand"
        showSingleOptionName
        placeholderLabel="All brands"
        options={brandOptions}
        selected={scope.brand}
        onChange={(brand) => onChange({ ...scope, brand })}
      />
      <MultiScopeSelectControl
        label="Platform"
        showSingleOptionName
        placeholderLabel="All platforms"
        options={platformOptions}
        selected={scope.platform}
        onChange={(platform) => onChange({ ...scope, platform })}
      />
      <MultiScopeSelectControl
        label="Ad Account"
        showSingleOptionName
        placeholderLabel="All ad accounts"
        options={accountOptions}
        selected={scope.account}
        onChange={(account) => onChange({ ...scope, account })}
      />
      {creativeTypeOptions === undefined ? null : (
        <MultiScopeSelectControl
          label="Creative Type"
          placeholderLabel="All creative types"
          options={creativeTypeOptions}
          selected={scope.creativeType}
          onChange={(creativeType) => onChange({ ...scope, creativeType })}
        />
      )}
      {extraControls}
      <DateRangeControl
        value={scope.range}
        onChange={(range) => onChange({ ...scope, range })}
        resolved={resolved}
      />
    </>
  );
}
